import apiClient from './apiClient';

export interface AnalyticsDashboard {
  period: string;
  summary: {
    totalUsers: number;
    activeUsers: number;
    totalPosts: number;
    totalPets: number;
    totalReports: number;
  };
  growth: {
    users: {
      current: number;
      previous: number;
      growth: number;
      trend: 'up' | 'down';
    };
    posts: {
      current: number;
      previous: number;
      growth: number;
      trend: 'up' | 'down';
    };
    pets: {
      current: number;
      previous: number;
      growth: number;
      trend: 'up' | 'down';
    };
    reports: {
      current: number;
      previous: number;
      growth: number;
      trend: 'up' | 'down';
    };
  };
}

export interface UserAnalytics {
  period: string;
  groupBy: string;
  dailyStats: Array<{
    _id: string;
    count: number;
    activeCount: number;
  }>;
  statusBreakdown: {
    total: number;
    active: number;
    banned: number;
    suspended: number;
    verified: number;
  };
}

export interface PostAnalytics {
  period: string;
  dailyPosts: Array<{
    _id: string;
    count: number;
    activeCount: number;
  }>;
  statusBreakdown: {
    total: number;
    active: number;
    inactive: number;
    suspended: number;
    rejected: number;
  };
  mostLikedPosts: Array<any>;
  mostCommentedPosts: Array<any>;
}

export interface MostReportedUser {
  user: {
    _id: string;
    username: string;
    name: string;
    email: string;
    profileImage?: string;
    isVerified: boolean;
    isBanned: boolean;
    isSuspended: boolean;
    createdAt: string;
  };
  reportCount: number;
  pendingCount: number;
  resolvedCount: number;
  types: string[];
}

export interface MostViewedProduct {
  _id: string;
  title: string;
  price: number;
  viewCount: number;
  contactCount: number;
  createdAt: string;
  owner: {
    _id: string;
    username: string;
    name: string;
    profileImage?: string;
  };
  petCategory: {
    _id: string;
    name: string;
  };
  breed?: {
    _id: string;
    name: string;
  };
  images: Array<{ url: string }>;
}

export interface CategoryAnalytics {
  petCategories: Array<{
    category: {
      name: string;
      slug: string;
      image?: string;
    };
    petCount: number;
    availableCount: number;
    totalViews: number;
    totalContacts: number;
    avgPrice: number;
  }>;
  postCategories: Array<{
    _id: string | null;
    postCount: number;
    totalLikes: number;
    totalComments: number;
  }>;
}

class AnalyticsService {
  async getDashboard(period: string = 'month') {
    const response = await apiClient.get(`/analytics/dashboard?period=${period}`);
    return response.data;
  }

  async getUserAnalytics(period: string = 'month', groupBy: string = 'day') {
    const response = await apiClient.get(`/analytics/users?period=${period}&groupBy=${groupBy}`);
    return response.data;
  }

  async getPostAnalytics(period: string = 'month') {
    const response = await apiClient.get(`/analytics/posts?period=${period}`);
    return response.data;
  }

  async getMostReportedUsers(limit: number = 10) {
    const response = await apiClient.get(`/analytics/most-reported-users?limit=${limit}`);
    return response.data;
  }

  async getMostViewedProducts(limit: number = 10, period: string = 'month') {
    const response = await apiClient.get(`/analytics/most-viewed-products?limit=${limit}&period=${period}`);
    return response.data;
  }

  async getCategoryAnalytics() {
    const response = await apiClient.get('/analytics/categories');
    return response.data;
  }
}

export const analyticsService = new AnalyticsService();
export default analyticsService;

